import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DmInboxItem,
  dmBlockFan,
  dmBroadcastList,
  dmInbox,
  dmReplyHeart,
  dmReplyHide,
  dmReplyPrivate,
} from "@/services/firebase/artistHub";
import { DM_INBOX_FILTERS, DM_PRIVATE_REPLY_MAX, DmInboxFilter, codePoints, groupByBroadcast, privateReplyText } from "@/lib/dm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useArtistConfirm } from "./ArtistConfirm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const hiddenByLabels = { artist: "團隊隱藏", ops: "營運隱藏", filter: "關鍵字隱藏" };

/**
 * C-01／C-03: 鐵粉私訊收件匣（contract §1d dmInbox / dmReplyHeart / dmReplyPrivate / dmReplyHide / dmBlockFan; v2.4 fields）.
 * Replies are grouped by broadcast; reading a page marks it seen (fans only see "藝人已看過這批回覆").
 * v2.4: broadcastId + filter are sent together (the backend filters inside one broadcast), `fanBlocked` comes with every
 * reply, filter "blocked" lists blocked fans, and dmBlockFan {blocked:false} unblocks.
 */
export default function ArtistDmInboxPanel({ artistId }: { artistId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>鐵粉私訊收件匣</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          鐵粉對廣播的回覆，只有藝人團隊看得到。可按愛心、私人回覆（只送進該粉絲的聊天室）、隱藏或封鎖（只限此藝人的私訊回覆）。
        </p>
        {!open ? (
          <Button variant="outline" onClick={() => setOpen(true)}>
            查看收件匣
          </Button>
        ) : (
          <InboxBody artistId={artistId} />
        )}
      </CardContent>
    </Card>
  );
}

function InboxBody({ artistId }: { artistId: string }) {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const [filter, setFilter] = useState<DmInboxFilter | "">("");
  const [broadcastId, setBroadcastId] = useState("");
  const [markSeen, setMarkSeen] = useState(true);
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const key = ["dm-inbox", artistId, filter, broadcastId, cursor];
  const list = useQuery({
    queryKey: key,
    queryFn: () => dmInbox(artistId, { filter: filter || null, broadcastId: broadcastId || null, cursor, markSeen }),
    retry: false,
  });
  const broadcasts = useQuery({
    queryKey: ["dm-broadcasts", artistId, "sent", null],
    queryFn: () => dmBroadcastList(artistId, { status: "sent", limit: 50 }),
    retry: false,
  });
  const byId = new Map((broadcasts.data?.items ?? []).map((b) => [b.broadcastId, b]));
  const [notice, setNotice] = useState("");
  // Optimistic overrides by uid until the next fetch (item.fanBlocked from dmInbox is the persisted truth).
  const [blockedOverride, setBlockedOverride] = useState<Map<string, boolean>>(new Map());
  const isBlocked = (item: DmInboxItem) => blockedOverride.get(item.uid) ?? item.fanBlocked === true;
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [reportOnHide, setReportOnHide] = useState(false);
  const patch = (replyId: string, change: Partial<DmInboxItem>) =>
    client.setQueryData<{ items: DmInboxItem[]; nextCursor: string | null }>(key, (old) =>
      old ? { ...old, items: old.items.map((i) => (i.replyId === replyId ? { ...i, ...change } : i)) } : old,
    );
  const heart = useMutation({
    mutationFn: (item: DmInboxItem) => dmReplyHeart(artistId, item.replyId, !item.hearted),
    onSuccess: (r) => patch(r.replyId, { hearted: r.hearted }),
  });
  const hide = useMutation({
    mutationFn: (item: DmInboxItem) => dmReplyHide(artistId, item.replyId, !item.hidden, { report: !item.hidden && reportOnHide }),
    onSuccess: (r) => {
      patch(r.replyId, { hidden: r.hidden, hiddenBy: r.hiddenBy, reported: r.reported });
      setNotice(r.hidden ? `已隱藏${r.reported ? "並送營運審核" : ""}（粉絲端看不出被隱藏）` : "已取消隱藏");
    },
  });
  const block = useMutation({
    mutationFn: ({ uid, value }: { uid: string; value: boolean }) => dmBlockFan(artistId, uid, value),
    onSuccess: (r) => {
      setBlockedOverride((m) => new Map(m).set(r.uid, r.blocked));
      void client.invalidateQueries({ queryKey: ["dm-inbox", artistId] });
      setNotice(r.blocked ? "已封鎖這位粉絲的私訊回覆（不影響帳號其他功能）" : "已解除封鎖");
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
    },
  });
  const busy = heart.isPending || hide.isPending || block.isPending;
  const error = heart.error ?? hide.error ?? block.error ?? (list.isError ? list.error : null);
  const reset = () => {
    setPages([null]);
    setBlockedOverride(new Map());
  };
  const groups = groupByBroadcast(list.data?.items ?? []);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="篩選回覆">
        {DM_INBOX_FILTERS.map((f) => (
          <Button
            key={f.value || "all"}
            size="sm"
            variant={filter === f.value ? "default" : "outline"}
            aria-pressed={filter === f.value}
            onClick={() => {
              setFilter(f.value);
              reset();
            }}
          >
            {f.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-2">
          指定廣播
          <select
            className="max-w-xs rounded border bg-background px-2 py-1"
            value={broadcastId}
            onChange={(e) => {
              setBroadcastId(e.target.value);
              reset();
            }}
          >
            <option value="">（不指定）</option>
            {(broadcasts.data?.items ?? []).map((b) => (
              <option key={b.broadcastId} value={b.broadcastId}>
                {fmt(b.sentAt)} · {(b.text ?? "").slice(0, 20) || b.type}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={markSeen} onChange={(e) => setMarkSeen(e.target.checked)} />
          讀取時標為已看
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={reportOnHide} onChange={(e) => setReportOnHide(e.target.checked)} />
          隱藏時同時檢舉給營運
        </label>
        <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => void list.refetch()}>
          重新整理
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-destructive">
          {errorText(error)}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {list.isPending && <p>讀取回覆…</p>}
      {broadcastId && filter && (
        <p className="text-xs text-muted-foreground">目前顯示：指定廣播 ＋「{DM_INBOX_FILTERS.find((f) => f.value === filter)?.label}」篩選（由後端篩選）。</p>
      )}
      {list.data && list.data.items.length === 0 && <p className="text-sm text-muted-foreground">沒有符合條件的回覆。</p>}
      {groups.map((g) => {
        const b = g.broadcastId ? byId.get(g.broadcastId) : undefined;
        return (
          <section key={g.broadcastId ?? "none"} className="rounded border">
            <header className="border-b bg-muted/50 px-3 py-2 text-sm">
              <span className="font-medium">{b ? (b.text ?? "（已收回）").slice(0, 60) || "（圖片／投票）" : g.broadcastId ? "較早的廣播" : "未對應廣播"}</span>
              {b && <span className="ml-2 text-xs text-muted-foreground">{fmt(b.sentAt)} · {b.senderKind === "artist" ? "本人" : "小編"}</span>}
            </header>
            <ul className="divide-y">
              {/* Oldest at the top, newest at the bottom (chat order). */}
              {[...g.items].reverse().map((item) => (
                <li key={item.replyId} className="space-y-2 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <strong>{item.nickname || "粉絲"}</strong>
                    {item.streakMonths ? <Badge variant="purple">鐵粉 {item.streakMonths} 個月</Badge> : null}
                    {!item.seen && <Badge variant="info">未看</Badge>}
                    {item.hidden && <Badge variant="neutral">{item.hiddenBy ? hiddenByLabels[item.hiddenBy] : "已隱藏"}</Badge>}
                    {item.reported && <Badge variant="warning">已檢舉</Badge>}
                    {isBlocked(item) && <Badge variant="danger">已封鎖</Badge>}
                    <span className="text-xs text-muted-foreground">{fmt(item.createdAt)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words">{item.text}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant={item.hearted ? "default" : "outline"} aria-pressed={item.hearted} disabled={busy} onClick={() => heart.mutate(item)}>
                      {item.hearted ? "♥ 已按愛心" : "♡ 愛心"}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setReplyTo(replyTo === item.replyId ? null : item.replyId)}>
                      私人回覆
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => hide.mutate(item)}>
                      {item.hidden ? "取消隱藏" : "隱藏"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        const value = !isBlocked(item);
                        const name = item.nickname || "粉絲";
                        confirm({
                          title: value ? `封鎖「${name}」` : `解除封鎖「${name}」`,
                          message: value
                            ? "對方將無法再回覆此藝人的私訊（仍可閱讀）。"
                            : "對方可以再次回覆此藝人的私訊。",
                          confirmText: value ? "封鎖" : "解除封鎖",
                          danger: value,
                          onConfirm: () => block.mutate({ uid: item.uid, value }),
                        });
                      }}
                    >
                      {isBlocked(item) ? "解除封鎖" : "封鎖"}
                    </Button>
                  </div>
                  {replyTo === item.replyId && (
                    <PrivateReply
                      artistId={artistId}
                      item={item}
                      onDone={(msg) => {
                        setReplyTo(null);
                        setNotice(msg);
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <div className="flex flex-wrap gap-2">
        {pages.length > 1 && (
          <Button size="sm" variant="outline" disabled={list.isFetching} onClick={reset}>
            回到最新
          </Button>
        )}
        {list.data?.nextCursor && (
          <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
            較舊的回覆
          </Button>
        )}
      </div>
      {dialog}
    </div>
  );
}

function PrivateReply({ artistId, item, onDone }: { artistId: string; item: DmInboxItem; onDone: (message: string) => void }) {
  const [text, setText] = useState("");
  const requestId = useRef(crypto.randomUUID());
  const send = useMutation({
    mutationFn: () => dmReplyPrivate(artistId, requestId.current, privateReplyText(text), { replyId: item.replyId }),
    onSuccess: (r) => onDone(`已私人回覆「${item.nickname || "粉絲"}」${r.message.from === "team" ? "（小編）" : ""}`),
  });
  const len = codePoints(text);
  return (
    <div className="space-y-2 rounded bg-muted/40 p-2">
      <Textarea rows={2} value={text} aria-label="私人回覆內容" placeholder="只有這位粉絲看得到" onChange={(e) => setText(e.target.value)} />
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${len > DM_PRIVATE_REPLY_MAX ? "text-destructive" : "text-muted-foreground"}`}>
          {len}／{DM_PRIVATE_REPLY_MAX} 字
        </span>
        <Button size="sm" disabled={send.isPending || !text.trim() || len > DM_PRIVATE_REPLY_MAX} onClick={() => send.mutate()}>
          送出私人回覆
        </Button>
      </div>
      {send.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorText(send.error)}
        </p>
      )}
    </div>
  );
}
